/**
 * Voice AI — Timezone Service
 * Centralizes date/time formatting.
 * Priority: voice_timezone_{userId} → voice_timezone → America/Chicago.
 *
 * Usage:
 *   import { tz } from '../services/timezone-service.js';
 *   tz.formatDate(isoString)   → "Mar 11, 2026"
 *   tz.formatTime(isoString)   → "10:15 AM"
 *   tz.formatDateTime(isoStr)  → "Mar 11, 2026 10:15 AM"
 *   tz.tz                      → "America/Chicago"  (sync getter)
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';
const SYSTEM_KEY = 'voice_timezone';
const USER_KEY = 'voice_user_timezone';
const DEFAULT_TZ = 'America/Chicago';

class TimezoneService {
  constructor() {
    this._systemTz = localStorage.getItem(SYSTEM_KEY) || null;
    this._userTz = localStorage.getItem(USER_KEY) || null;
    this._loadPromise = null;
  }

  /** Load timezones from API. Called once on import. */
  load() {
    if (this._loadPromise) return this._loadPromise;
    this._loadPromise = (async () => {
      try {
        const res = await authService.apiFetch(`${API_BASE}/general-config.php`);
        const data = await res.json();
        if (data?.config) {
          // System/business timezone
          if (data.config.voice_timezone) {
            this._systemTz = data.config.voice_timezone;
            localStorage.setItem(SYSTEM_KEY, this._systemTz);
          }
          // Per-user timezone: voice_timezone_{userId}
          const user = authService.getUser();
          const userKey = user?.id ? `voice_timezone_${user.id}` : null;
          const userVal = userKey ? data.config[userKey] : null;
          if (userVal) {
            this._userTz = userVal;
            localStorage.setItem(USER_KEY, userVal);
          } else {
            this._userTz = null;
            localStorage.removeItem(USER_KEY);
          }
        }
      } catch (e) {
        // Keep cached values or fallback
      }
    })();
    return this._loadPromise;
  }

  /** Active timezone: user → system → default. */
  get tz() {
    return this._userTz || this._systemTz || DEFAULT_TZ;
  }

  /** Update system timezone (called when admin saves business timezone). */
  set(timezone) {
    this._systemTz = timezone;
    localStorage.setItem(SYSTEM_KEY, timezone);
  }

  /** Set per-user timezone (called when user saves their preference). */
  setUser(timezone) {
    if (timezone) {
      this._userTz = timezone;
      localStorage.setItem(USER_KEY, timezone);
    } else {
      this._userTz = null;
      localStorage.removeItem(USER_KEY);
    }
  }

  formatDate(dateStr, opts = {}) {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: this.tz, month: 'short', day: 'numeric', year: 'numeric',
      ...opts
    });
  }

  formatTime(dateStr, opts = {}) {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      timeZone: this.tz, hour: 'numeric', minute: '2-digit',
      ...opts
    });
  }

  formatDateTime(dateStr) {
    if (!dateStr) return '--';
    return this.formatDate(dateStr) + ' ' + this.formatTime(dateStr);
  }
}

export const tz = new TimezoneService();
tz.load();
